import { Matrix4 } from "../../../imports/avm";
import { VisibilityState } from "../MeshCollectionBase";
import { Node3D } from "./Node3D";
import { Model } from "../../../application/Model";

/** @import { BufferGeometry, Material } from "../../../imports/avs" */

// Module-level temps for MeshNode3D.
// _tempLocalMatrix: compose pos/quat/scale into local matrix (consumed before child recursion).
// _tempWorldMatrix: compute world matrix to write to mesh collection.
// _sharedReadMatrix: used internally to read parent world matrices without allocation.
const _tempLocalMatrix = new Matrix4();
const _tempWorldMatrix = new Matrix4();
const _sharedReadMatrix = new Matrix4();

/**
 * Scene graph node that represents a mesh.
 * @extends {Node3D}
 */
export class MeshNode3D extends Node3D {

  /** @type {number | null} */
  #meshId = null;

  /**
   * Creates a new detached mesh node based on the given geometry and material.
   * @param {BufferGeometry} [geometry]
   * @param {Material} [material]
   */
  constructor(geometry, material) {
    super(true); // skip _matrixWorld own property — world transform lives in the mesh collection

    this._detachedState.geometry = geometry;
    this._detachedState.material = material;
  }

  /** @returns {number | null} The mesh ID in the mesh collection, or null if detached. */
  get meshId() {
    return this.#meshId;
  }

  /**
   * Reads the world transform matrix into {@link target}.
   * When attached, reads from the mesh collection (source of truth).
   * When detached, copies from detachedState.
   *
   * @param {Matrix4} [target] Matrix to write into. Recommended for safe, allocation-free reads.
   * @returns {Matrix4} The target filled with the world matrix, or a new matrix if no target is provided.
   */
  getTransformWorld(target) {
    if (this.#meshId !== null && this._meshCollection) {
      const out = target || new Matrix4();
      this._meshCollection.getTransform(this.#meshId, out);
      return out;
    }
    const mw = this._detachedState.matrixWorld ??= new Matrix4();
    return target ? target.copy(mw) : mw.clone();
  }

  /**
   * @returns {VisibilityState} The visibility state.
   */
  getVisibilityState() {
    return this._detachedState ? this._detachedState.visibilityState : this._meshCollection?.getVisibilityState(this.#meshId);
  }

  /**
   * Sets the visibility state for this node and its children.
   * @param {VisibilityState} value
   * @returns {void}
   */
  setVisibilityState(value) {
    if (!this._detachedState) {
      this._meshCollection.setVisibilityState(this.#meshId, value);
    }
    super.setVisibilityState(value);
  }

  /**
   * Initializes this node with the given mesh collection.
   * @param {MeshCollectionBase} meshCollection
   * @param {boolean} [updateMatrices=true] If true, updateTransformWorld is called after initialization.
   * @returns {this}
   */
  _initialize(meshCollection, updateMatrices = true) {
    // If we're initializing from an existing mesh (fromMesh case), don't update matrices
    // in super._initialize - we need to read the mesh's transform first before any updates
    const initFromExistingMesh = this.#meshId !== null;

    super._initialize(meshCollection, initFromExistingMesh ? false : updateMatrices);

    const tmpState = this._detachedState;
    this._detachedState = undefined;

    // Create fragment on first model assignment if we have enough info
    if (tmpState) {
      if (this.#meshId === null && tmpState.geometry && tmpState.material) {
        // initialize node based on geometry and material
        // Note: matrixWorld is in tmpState.matrixWorld (meshId still null), updated by super._initialize

        const createdId = this._meshCollection.add(tmpState.geometry, tmpState.material, tmpState.matrixWorld);
        if (createdId !== undefined) {
          this.#meshId = createdId;

          // Apply visibility state that was set before initialization
          this._meshCollection.setVisibilityState(this.#meshId, tmpState.visibilityState);
        }
      } else if (initFromExistingMesh) {
        // initialize node based on existing mesh - read transform from mesh
        this._meshCollection.getTransform(this.#meshId, _tempWorldMatrix);
        const parentWorld = this.parent?.getTransformWorld(_sharedReadMatrix);
        if (parentWorld) {
          _tempLocalMatrix.copy(parentWorld).invert().multiply(_tempWorldMatrix);
        } else {
          _tempLocalMatrix.copy(_tempWorldMatrix);
        }
        _tempLocalMatrix.decompose(this.position, this.quaternion, this.scale);

        // Don't call updateTransformWorld - the mesh already has the correct transform
      }
    }

    return this;
  }

  /**
   * @returns {Material} The material of the mesh represented by this node.
   */
  getMaterial() {
    return this._detachedState ? this._detachedState.material : this._meshCollection?.getMaterial(this.#meshId);
  }

  /**
   * Sets material on the mesh represented by this node.
   * @param {Material} material
   * @returns {void}
   */
  setMaterial(material) {
    if (this._detachedState) {
      this._detachedState.material = material;
      return;
    }

    this._meshCollection?.setMaterial(this.#meshId, material);
  }

  /**
   * @returns {BufferGeometry} The geometry of the mesh represented by this node.
   */
  getGeometry() {
    return this._detachedState ? this._detachedState.geometry : this._meshCollection?.getGeometry(this.#meshId);
  }

  /**
   * Sets the geometry of the mesh represented by this node.
   * @param {BufferGeometry} geometry
   * @returns {void}
   */
  setGeometry(geometry) {
    if (this._detachedState) {
      this._detachedState.geometry = geometry;
      return;
    }
    this._meshCollection?.setGeometry(this.#meshId, geometry);
  }

  /**
   * Detaches this node from the mesh collection.
   * @param {number[]} meshIdsToRemove - Array to gather Mesh IDs that should be removed from the mesh collection.
   * @internal
   */
  _detachFromMeshCollection(meshIdsToRemove) {
    const meshCollection = this._meshCollection;

    super._detachFromMeshCollection(meshIdsToRemove);

    if (meshCollection && this.#meshId !== null) {
      // Save world matrix and mesh data into _detachedState (re-created by super call)
      this._detachedState.matrixWorld = new Matrix4();
      meshCollection.getTransform(this.#meshId, this._detachedState.matrixWorld);
      this._detachedState.geometry = meshCollection.getGeometry(this.#meshId);
      this._detachedState.material = meshCollection.getMaterial(this.#meshId);
      meshIdsToRemove.push(this.#meshId);
    }

    this.#meshId = null;
  }

  /**
   * Updates world matrix and applies it to the represented fragment.
   * Computations are performed immediately and propagated to all affected descendants.
   *
   * @param {boolean} [force=false] If true, forces update regardless of dirty flag.
   * @param {Matrix4} [_parentWorldMatrix=null] Internal: parent's world matrix passed during recursion.
   * @returns {void}
   */
  updateTransformWorld(force = false, _parentWorldMatrix = null) {
    const needsUpdate = this._dirty || force;

    if (needsUpdate) {
      this.getTransformLocal(_tempLocalMatrix);

      const parentWorld = _parentWorldMatrix || this.parent?.getTransformWorld(_sharedReadMatrix);

      if (this.#meshId !== null && this._meshCollection) {
        // Attached: compute world matrix into temp, write directly to mesh collection
        if (parentWorld) {
          _tempWorldMatrix.multiplyMatrices(parentWorld, _tempLocalMatrix);
        } else {
          _tempWorldMatrix.copy(_tempLocalMatrix);
        }
        this._meshCollection.setTransform(this.#meshId, _tempWorldMatrix);
      } else {
        // Detached (e.g. during initialization before meshId is assigned):
        // store in _detachedState.matrixWorld (lazily allocated)
        const mw = this._detachedState.matrixWorld ??= new Matrix4();
        if (parentWorld) {
          mw.multiplyMatrices(parentWorld, _tempLocalMatrix);
        } else {
          mw.copy(_tempLocalMatrix);
        }
      }

      this._dirty = false;
    }

    // Only traverse children if this node was updated OR subtree has dirty nodes
    if (needsUpdate || this._subtreeDirty) {

      for (const child of this.children) {
        // For attached nodes, re-read world matrix from mesh collection before each child.
        // This avoids the aliased-temp problem: child recursion may overwrite _tempWorldMatrix,
        // so we fetch a fresh copy for each sibling.
        if (this.#meshId !== null && this._meshCollection) {
          this._meshCollection.getTransform(this.#meshId, _tempWorldMatrix);
          child.updateTransformWorld(needsUpdate, _tempWorldMatrix);
        } else {
          child.updateTransformWorld(needsUpdate, this._detachedState.matrixWorld);
        }
      }
      this._subtreeDirty = false;
    }
  }

  /**
   * Creates a new node from an existing mesh in the mesh collection.
   * @param {number} meshId
   * @param {Model|Node3D} modelOrParentNode Parent node to which the new node should be added. If a model is provided the new node will be added to the scene graph root.
   * @returns {MeshNode3D} The new node.
   */
  static fromMesh(meshId, modelOrParentNode) {
    let parent = modelOrParentNode instanceof Model ? modelOrParentNode.getMeshCollection().getSceneGraph() : modelOrParentNode;

    if (parent?.isAttached()) {
      const node = new MeshNode3D();
      node.#meshId = meshId;
      parent.add(node);
      return node;
    } else {
      throw new Error("Cannot create MeshNode3D from mesh: parent node must be attached to a MeshCollection3D");
    }
  }
}